import { Button } from '@components/buttons'
import { SelectInput, TextInput } from '@components/inputs'
import { yupResolver } from '@hookform/resolvers/yup'
import { Box } from '@mui/material'
import {
  CreateExamTypeDto,
  ExamType,
  Site,
  UpdateExamTypeDto,
} from '@services/api'
import { useSelector } from '@state/store'
import { findAll as findAllSites } from '@state/thunks/siteThunk'
import { examTypeSchema } from '@utils/schemas'
import { MouseEventHandler, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { createType, updateType } from '../../../state/thunks/procedureThunk'
import ExamSelectionTable from './ExamSelectionTable'

type AddExamTypeFormProps = {
  row?: ExamType
  onClose?: () => void
  onCancelButtonPress?: (
    ev: MouseEventHandler<HTMLButtonElement> | undefined,
  ) => void
}

export function ExamTypeForm({ row, onClose }: AddExamTypeFormProps) {
  const dispatch = useDispatch()
  const [selectedExams, setSelectedExams] = useState<number[]>([])

  const {
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm<ExamType>({
    resolver: yupResolver(examTypeSchema),
    defaultValues: {
      ...row,
    },
  })
  const onSubmit = (values: ExamType) => {
    if (row?.id) {
      const updateExamTypeDto: UpdateExamTypeDto = {
        ...values,
        exams: selectedExams.map((_examId) => ({ id: _examId })),
      }
      dispatch(updateType({ id: row.id, body: updateExamTypeDto }))
    } else {
      const createExamTypeDto: CreateExamTypeDto = {
        ...values,
        exams: selectedExams.map((_examId) => ({ id: _examId })),
      }
      dispatch(createType(createExamTypeDto))
    }
    if (onClose) onClose()
  }

  // Reducers
  const { user } = useSelector(({ auth }) => ({
    user: auth.user,
  }))
  const sites = (user as unknown as { sites: Site[] })?.sites || []
  // Map all sites
  const sitesOptions = useMemo(
    () =>
      sites.map((site) => ({
        value: site.id as number,
        label: site.label,
      })),
    [sites],
  )
  useEffect(() => {
    reset(row ? { ...row } : ({} as ExamType))
  }, [row, reset])

  useEffect(() => {
    if (row) {
      const ids = row.exams.map((_exam) => _exam.id)
      setSelectedExams(ids)
    } else {
      setSelectedExams([])
    }
  }, [row])

  useEffect(() => {
    dispatch(findAllSites())
  }, [])

  const _onSelectedExamsChange = (values: number[] | string[]) => {
    setSelectedExams(values as number[])
  }

  return (
    <Box
      minWidth="800px"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        padding: 2,
      }}
    >
      <Box display="flex" gap={1}>
        <SelectInput
          name="siteId"
          options={sitesOptions}
          control={control}
          errors={errors}
        />
        <TextInput name="code" errors={errors} control={control} />
        <TextInput
          name="label"
          placeholder="label"
          errors={errors}
          control={control}
        />
      </Box>
      <ExamSelectionTable
        selectedRows={selectedExams}
        onSelectionChange={_onSelectedExamsChange}
      />

      <Box display="flex" width="50%" alignSelf="flex-end" gap={2}>
        {onClose && (
          <Button
            onClick={onClose}
            color="secondary"
            text="cancel"
            textColor="white"
            fullWidth
          />
        )}
        <Button
          onClick={handleSubmit(onSubmit)}
          color="primary"
          text="save"
          textColor="white"
          fullWidth
        />
      </Box>
    </Box>
  )
}
